import {defineStore} from 'pinia'
import {GetCubeInputData, GetCubesBrowser, getExternalCubes, GetStrategyCubes} from '@/api/cube.js'
import {Cube, CubeInputData, CubesBrowser, ExternalCubes} from '@/models/Cubes'
import {Optional} from '@/models/TypeHelpers'
import { FESettingsInstance } from '@/auth/FESettings'

interface CubeInputsPayload {
  cubeKey: string;
  versionKey: string;
  forceReload: boolean;
}

export const useCubesStore = defineStore('cubes', {
  state: () => ({
    cubes: null as Optional<Cube[]>,
    cubesBrowser: null as Optional<CubesBrowser>,
    externalCubes: [] as ExternalCubes[],
    cubeInputData: null as Optional<CubeInputData>,
  }),
  getters: {
    metricFormat: (state) => state.cubesBrowser?.metric_format,
    cubeKeysToNames: (state): Map<string, string> => {
      if (!state.cubesBrowser?.cube_keys || !state.cubes) {
        return new Map()
      }
      const m = new Map<string, string>()
      for (const key of state.cubesBrowser.cube_keys) {
        const keyNum = Number(key)
        const cube = state.cubes.find(d => Number(d.cube_key) === keyNum)
        m.set(key, cube?.cube_name || key)
      }
      return m
    }
  },
  actions: {
    $reset () {
      this.cubes = null
      this.cubesBrowser = null
      this.cubeInputData = null
    },
    async loadCubes () {
      try {
        this.cubes = await GetStrategyCubes()
      } catch (e) {
        console.error(e)
      }
    },
    async loadCubesBrowser (withExternals = false) {
      try {
        this.cubesBrowser = await GetCubesBrowser()
        if(withExternals && !this.externalCubes?.length) {
          const externalCubes = await getExternalCubes()
          this.externalCubes = externalCubes.map((item: ExternalCubes) => ({
            ...item,
            dimensions: item.dimensions.map((dimension: string) => ({name: dimension, values: item.properties[dimension]})),
          }))
        }
      } catch (e) {
        console.error(e)
      }
    },
    async getAndLoadCubeInputs (payload: CubeInputsPayload) {
      if(payload.cubeKey === 'external'){
        const data = this.externalCubes.find(item=> item.budgets_external_key === payload.versionKey)

        this.cubeInputData = data as unknown as CubeInputData
        FESettingsInstance.setItem('cubeBrowserData', JSON.stringify(data))
        return data
      }

      // Caching disabled, at least for now
      // try {
      //   const cubeBrowserData = JSON.parse(FESettingsInstance.getItem('cubeBrowserData') || 'null')
      //   if (cubeBrowserData && !payload.forceReload) {
      //     this.cubeInputData = cubeBrowserData
      //     return cubeBrowserData
      //   }
      // } catch (e) {
      //   console.error(e)
      // }

      // If we don't have the data in the local storage, or we want to force a reload, we fetch the data from the API
      try {
        const response = await GetCubeInputData(payload.cubeKey, payload.versionKey)
        const enrichedResponse = {
          ...response,
          cube_key: response?.cube_key || payload.cubeKey,
          version_key: response?.version_key || payload.versionKey
        }
        this.cubeInputData = enrichedResponse
        // Caching disabled, at least for now
        // FESettingsInstance.setItem('cubeBrowserData', JSON.stringify(enrichedResponse))
        return enrichedResponse
      } catch (e) {
        console.error(e)
      }
    },
  },
})
